import { useNavigate } from "react-router";
import { Ban, CheckCircle2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { useConfirm } from "@/lib/confirm";
import { usePermissao } from "@/lib/use-permissao";
import { SecaoComplementar } from "./dados/SecaoComplementar";
import { SecaoContato } from "./dados/SecaoContato";
import { SecaoEndereco } from "./dados/SecaoEndereco";
import { SecaoIdentificacao } from "./dados/SecaoIdentificacao";
import { useSalvarPaciente } from "./dados/use-salvar-paciente";

type Props = {
  patient: any;
  onSaved: () => void;
};

const botao = "inline-flex h-9 items-center gap-1.5 rounded-md border bg-white px-3.5 text-xs font-medium transition-colors disabled:opacity-60 dark:bg-card";

export function PatientDataTab({ patient, onSaved }: Props) {
  const podeEditar = usePermissao("patients.manage");
  const salvar = useSalvarPaciente(patient.id, onSaved);
  const confirm = useConfirm();
  const navigate = useNavigate();
  const utils = trpc.useUtils();

  const aoMudarStatus = (msg: string) => () => {
    toast.success(msg);
    utils.patient.list.invalidate();
    utils.patient.listMineAcrossOrgs.invalidate();
    onSaved();
  };
  const deactivate = trpc.patient.deactivate.useMutation({
    onSuccess: aoMudarStatus("Paciente inativado"),
    onError: (e) => toast.error(e.message || "Erro ao inativar"),
  });
  const activate = trpc.patient.activate.useMutation({
    onSuccess: aoMudarStatus("Paciente reativado"),
    onError: (e) => toast.error(e.message || "Erro ao reativar"),
  });
  const remove = trpc.patient.remove.useMutation({
    onSuccess: () => {
      toast.success("Paciente excluído");
      utils.patient.list.invalidate();
      utils.patient.listMineAcrossOrgs.invalidate();
      navigate("/pacientes");
    },
    onError: (e) => toast.error(e.message || "Erro ao excluir"),
  });

  const inativar = async () => {
    const ok = await confirm({
      title: `Inativar ${patient.name}?`,
      description: "O paciente sai da lista padrão mas mantém histórico. Você pode reativar depois.",
      confirmText: "Inativar",
      cancelText: "Cancelar",
    });
    if (ok) deactivate.mutate({ id: patient.id });
  };

  const excluir = async () => {
    const ok = await confirm({
      title: `Excluir ${patient.name}?`,
      description: "Essa ação é permanente. Todos os dados, consultas e anexos do paciente serão removidos.",
      confirmText: "Excluir paciente",
      cancelText: "Cancelar",
      destructive: true,
    });
    if (ok) remove.mutate({ id: patient.id });
  };

  const secao = { patient, salvar, somenteLeitura: !podeEditar };

  return (
    <div className="space-y-4">
      <div className="grid gap-4 lg:grid-cols-3">
        <SecaoIdentificacao {...secao} />
        <SecaoContato {...secao} />
        <SecaoEndereco {...secao} />
      </div>
      <SecaoComplementar {...secao} />

      {podeEditar && (
        <div className="flex flex-wrap items-center justify-end gap-2 pt-1">
          {patient.active ? (
            <button
              type="button"
              onClick={inativar}
              disabled={deactivate.isPending}
              className={`${botao} border-zinc-200 text-zinc-600 hover:border-amber-300 hover:bg-amber-50 hover:text-amber-700 dark:border-border dark:text-muted-foreground`}
            >
              <Ban className="h-3.5 w-3.5" />
              Inativar paciente
            </button>
          ) : (
            <button
              type="button"
              onClick={() => activate.mutate({ id: patient.id })}
              disabled={activate.isPending}
              className={`${botao} border-zinc-200 text-zinc-600 hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-700 dark:border-border dark:text-muted-foreground`}
            >
              <CheckCircle2 className="h-3.5 w-3.5" />
              Reativar paciente
            </button>
          )}
          <button
            type="button"
            onClick={excluir}
            disabled={remove.isPending}
            className={`${botao} border-red-200 text-red-600 hover:bg-red-50 dark:border-red-500/30 dark:hover:bg-red-500/10`}
          >
            <Trash2 className="h-3.5 w-3.5" />
            Excluir paciente
          </button>
        </div>
      )}
    </div>
  );
}
